<template>
  <div id="app" class="flex flex-col">
    <div v-if="!['login', 'register', 'emailconfirmation'].includes($route.path.toLowerCase().slice(1))" :class="[
      'header w-full',
      'header-sidebar-color'
    ]">
      <div class="header-content">
        <div class="flex-1 ml-5">
          <button v-show="!isExpanded && isMobile" class="menu-toggle" @click="handleToggleMenu">
            <span class="space-icon space-icon-expand" />
          </button>
        </div>
        <img src="@/assets/aff-space-logo.png" alt="Logo" class="h-10 w-auto p-1">
        <div v-if="isAuthenticated" class="flex-1 flex justify-end mr-8">
          <!-- <Button v-if="!isAdmin" v-tooltip.bottom="'Need help?'" icon="pi pi-question-circle" @click="goToHelp"
            class="h-6 mr-2" severity="primary" outlined /> -->
          <Button v-tooltip.bottom="'Logout'" icon="pi pi-fw pi-sign-out" @click="logout" class="h-6 w-5"
            variant="outlined" severity="primary" />
        </div>
      </div>
      <div v-if="isImpersonated" class="h-5 bg-yellow-900 w-full mb-2 flex items-center justify-center gap-4">
        <span class="text-white">Logged in as {{ name }}</span>
        <div 
            @click="backToAdmin"
            class="text-yellow-300 hover:text-yellow-100 cursor-pointer text-sm font-medium transition-colors duration-150 underline"
        >
            Back to Admin
        </div>
    </div>
    </div>

    <div class="flex flex-row flex-1 overflow-auto main-bg-color ">
      <Sidebar ref="sidebarRef" :isExpanded="isExpanded" :isMobile="isMobile" @toggle="handleToggleMenu" />
      <div 
        class="main-content flex-grow"
        :class="{ 'overlay': isMobile && isExpanded }"
        @click="isMobile && isExpanded ? handleToggleMenu() : null"
      >
        <div class="content-wrapper">
          <router-view />
        </div>
      </div>
    </div>
  </div>

</template>


<script setup>
import "/node_modules/flag-icons/css/flag-icons.min.css";
import '@fortawesome/fontawesome-free/css/all.css'
import { computed, onMounted, ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import AuthService from './services/AuthService';
import Sidebar from './components/SideBar.vue'
import logoPath from '@/assets/cropped-aff-space-logo.png';

const store = useStore();
const router = useRouter();
const isAuthenticated = computed(() => store.getters.isAuthenticated);
const isAdmin = computed(() => store.getters.isAdmin);
const name = computed(() => store.getters.name);

const isImpersonated = computed(() => store.getters.isImpersonated);

const headerHeight = computed(() => {
  return isAuthenticated.value ? '55px' : '0px' // Adjust these values based on your actual header heights
})

const logout = async () => {
  try {
    store.dispatch('setUser', null);
    localStorage.removeItem('token'); // Remove the JWT token
    router.push({ name: 'Login' }); // Redirect to the login page

  } catch (err) {
    console.log('Err:', err);
  }
};

const goToHelp = () => {
  router.push({
    name: 'Settings',
    query: { showHelp: 'true' }
  });
};

const backToAdmin = async() => {
  try{
    const response = await AuthService.backToAdmin();
    localStorage.setItem('token', response.data.token);
    await store.dispatch('setUser', response.data.mappedUser);
    // Return to the publisher's page we logged in from, if we have it; else home.
    let returnUserId = null;
    try {
      returnUserId = localStorage.getItem('adminReturnUserId');
      localStorage.removeItem('adminReturnUserId');
    } catch (e) { /* storage unavailable */ }
    router.push(returnUserId ? `/users/${returnUserId}` : '/');
  }catch(err){
    console.log('Err:', err);
  }
};

const is_expanded_key = "is_expanded";
const isExpanded = ref(localStorage.getItem(is_expanded_key) === "true");

const handleToggleMenu = (value = null) => {
  // Only use value if it's a boolean, otherwise toggle
  isExpanded.value = typeof value === 'boolean' ? value : !isExpanded.value;
  localStorage.setItem(is_expanded_key, isExpanded.value);
};

const isMobile = ref(window.innerWidth <= 768);

// Throttle function
const throttle = (fn, delay) => {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      fn(...args);
      lastCall = now;
    }
  };
};

// Throttled resize handler
const handleResize = throttle(() => {
  isMobile.value = window.innerWidth <= 768;
}, 500); // Only runs every 500ms max

onMounted(() => {
  // Set favicon
  const favicon = document.querySelector("link[rel='icon']") || document.createElement('link');
  favicon.type = 'image/png';
  favicon.rel = 'icon';
  favicon.href = logoPath;
  if (!document.querySelector("link[rel='icon']")) {
    document.head.appendChild(favicon);
  }
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

</script>

<style>
:root {
  --dark-alt: #15171c;
  --primary: #198ebf;
  --background-color: rgb(242 245 249);
  --advertiser-background-color: hsl(222deg 30.13% 26.45%);
  --admin-background-color: hsl(252deg 6.17% 15.88%);
  --selected-row-color: #99780b;
  --collapsed-sidebar-width: 3.5rem;
  --expanded-sidebar-width: 11rem;
  --secondary-background-color: #18181B;
  --primary-text-color: #D8D8D8;
  --header-height: v-bind('headerHeight');
}

/* --p-datatable-body-cell-border-color: #3B3B3B !important; */
body {
  font-family: 'ProximaNova', system-ui, -apple-system, sans-serif;
  margin: 0;
  height: 100svh;
  overflow: hidden;
}

#app {
  height: 100svh;
  width: 100%;
}

.header {
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
}

.main-content {
  width: calc(100vw - var(--collapsed-sidebar-width));
  transition: width 0.3s ease;
  color: var(--primary-text-color);
}

/* Using the adjacent sibling combinator (+) */
.sideBar-expanded+.main-content {
  width: calc(100vw - var(--expanded-sidebar-width));
}

.content-wrapper {
  /* height: 100%; */
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  height: 55px;
}



.logo-logged-out {
  height: 2rem;
  width: 2rem;
}

.auth-menu .p-menubar {
  background: transparent;
  border: none;
  color: white;
}

.auth-menu .p-menuitem-link {
  color: white;
  /* Set text color for all menu items */
  margin-right: 10px;
  /* Space between menu items */
}

.sideBar .p-menu.p-component.auth-menu.vertical-menu {
  min-width: 0 !important;
  width: auto !important;
}

.p-tag-secondary {
  background: #b7c8da !important;
}

.primevue-multiselect-border {
  border: 1px solid var(--p-multiselect-border-color)
}

.error {
  color: red;
  /* Error text color */
  font-size: 0.85rem;
  margin-top: 0.5rem;
}


.selected-month {
  background-color: #007bff !important;
  /* Change background color when clicked */
  color: white !important;
  border: 1px solid #0056b3 !important;
}

.p-button-secondary:hover {
  background-color: #007bff !important;
  color: white !important;
  /* Optionally adjust text color */
  border-color: #0056b3 !important;
  /* Optionally adjust border color */
}

.income-cell {
  color: hsl(134, 89%, 46%);
  /* Light green background */
  padding: 0 !important;
  /* Force padding to be 0 */
  border-radius: 4px;
  /* Rounded corners */
  width: 100%;
  /* Take full width of the parent */
  height: 100%;
  /* Take full height of the parent */
  display: flex;
  /* Flexbox layout */
  align-items: center;
}

.subheader {
  font-size: 1.3rem;
  font-weight: bold;
}

.subheader-border {
  border-bottom: 2px solid #ccc;
  padding-bottom: 10px;
}

@keyframes blip {
  0% {
    background-color: var(--selected-row-color);
  }

  50% {
    background-color: transparent;
  }

  100% {
    background-color: var(--selected-row-color);
  }
}

.selected-row {
  background-color: var(--selected-row-color) !important;
  color: black !important;
}

.p-datatable-tbody {
  background-color: var(--card-background-color) !important;
}

.font-bolyar {
  font-family: FMBolyarSansPro;
}

.overlay {
  position: relative;
  pointer-events: all;
}

.overlay::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}
</style>
